import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, Navigation, Search } from "lucide-react";
import { toast } from "sonner";

// Fix Leaflet's broken default marker icon paths under bundlers.
const goldIcon = L.divIcon({
  className: "wt-map-pin",
  html: `<div style="width:34px;height:34px;transform:translate(-50%,-100%);display:flex;align-items:center;justify-content:center;filter:drop-shadow(0 4px 8px rgba(0,0,0,.55))">
    <svg viewBox="0 0 24 24" width="34" height="34" fill="#C9A24B" stroke="#0A0A0A" stroke-width="1.4"><path d="M12 2C7.6 2 4 5.6 4 10c0 5.4 7 12 7.3 12.3.4.3 1 .3 1.4 0C13 22 20 15.4 20 10c0-4.4-3.6-8-8-8zm0 11a3 3 0 110-6 3 3 0 010 6z"/></svg>
  </div>`,
  iconSize: [34, 34], iconAnchor: [17, 34],
});

export default function MapPinPicker({ lat, lng, seedQuery, onChange }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [query, setQuery] = useState(seedQuery || "");
  const [searching, setSearching] = useState(false);

  // init map once
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const start = [lat ?? 20.5937, lng ?? 78.9629]; // India centroid
    const zoom = (lat && lng) ? 15 : 4;
    const map = L.map(containerRef.current, { zoomControl: true, attributionControl: false })
      .setView(start, zoom);
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19, attribution: "© OpenStreetMap",
    }).addTo(map);
    L.control.attribution({ prefix: false }).addAttribution("© OSM").addTo(map);
    map.on("click", (e) => {
      const { lat, lng } = e.latlng;
      placeMarker(lat, lng);
      onChange?.({ lat, lng });
    });
    mapRef.current = map;

    if (lat != null && lng != null) placeMarker(lat, lng);
    else if (seedQuery) doSearch(seedQuery, true);

    // fix layout after mount
    setTimeout(() => map.invalidateSize(), 60);
    return () => { map.remove(); mapRef.current = null; };
    // eslint-disable-next-line
  }, []);

  const placeMarker = (la, ln) => {
    const map = mapRef.current;
    if (!map) return;
    if (markerRef.current) markerRef.current.setLatLng([la, ln]);
    else markerRef.current = L.marker([la, ln], { icon: goldIcon, draggable: true })
      .addTo(map)
      .on("dragend", (ev) => {
        const p = ev.target.getLatLng();
        onChange?.({ lat: p.lat, lng: p.lng });
      });
  };

  const doSearch = async (q, silent = false) => {
    const text = (q || query).trim();
    if (!text) return;
    setSearching(true);
    try {
      const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(text)}`);
      const arr = await r.json();
      if (!arr?.length) {
        if (!silent) toast.error("Could not find that address — try including the city");
        return;
      }
      const la = parseFloat(arr[0].lat), ln = parseFloat(arr[0].lon);
      mapRef.current?.setView([la, ln], 16);
      placeMarker(la, ln);
      onChange?.({ lat: la, lng: ln });
    } catch {
      if (!silent) toast.error("Search failed — please try again");
    } finally { setSearching(false); }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("Location not available on this device");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        mapRef.current?.setView([latitude, longitude], 16);
        placeMarker(latitude, longitude);
        onChange?.({ lat: latitude, lng: longitude });
      },
      () => toast.error("Permission denied — allow location access"),
      { enableHighAccuracy: true, timeout: 8000 },
    );
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="mappin-search-input"
            value={query} onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); doSearch(); } }}
            placeholder="Search your shop address"
            className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-[#0A0A0A] border border-[#27272A] text-sm text-zinc-100 focus:outline-none focus:border-[#C9A24B]" />
        </div>
        <button type="button" data-testid="mappin-search-btn" onClick={() => doSearch()} disabled={searching}
          className="px-3 py-2.5 rounded-xl bg-[#1C1C1E] border border-[#27272A] text-sm text-zinc-200 hover:border-[#C9A24B]/50 disabled:opacity-50">
          Find
        </button>
      </div>
      <button type="button" data-testid="mappin-use-my-location" onClick={useMyLocation}
        className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-[#1C1C1E] border border-[#27272A] text-xs text-zinc-300 hover:border-[#C9A24B]/50">
        <Navigation className="w-3.5 h-3.5 text-[#C9A24B]" /> Use my current location
      </button>
      <div ref={containerRef} data-testid="mappin-canvas"
        className="w-full h-[280px] rounded-xl overflow-hidden border border-[#27272A] bg-[#0A0A0A]" />
      <p className="text-[11px] text-zinc-500 flex items-center gap-1.5">
        <MapPin className="w-3 h-3 text-[#C9A24B]" /> Tap the map to drop or move the pin. Drag the pin to fine-tune.
      </p>
    </div>
  );
}
